import {useIsMobile} from "@bounce/shared/consumer/helpers";
import {
  getIsDateValid,
  getMaxDateToReschedule,
  getShouldDisableDate,
  ReschedulePlanDialogContent,
} from "@bounce/shared/consumer/pages/account-dashboard/dialogs";
import {PaymentPlanFrequency} from "@bounce/shared/enums";
import NiceModal, {NiceModalHocProps, useModal} from "@ebay/nice-modal-react";
import dayjs, {Dayjs} from "dayjs";
import React, {useEffect, useMemo, useState} from "react";
import {toast} from "sonner";
import {useUpdateFutureInstallmentDate} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";

export interface ReschedulePlanDialogProps extends NiceModalHocProps {
  frequency: PaymentPlanFrequency;
  installmentAmountCents: number;
  originalDueDate: Dayjs;
  paymentPlanId: string;
  accountId: string;
}

const ReschedulePlanDialog = NiceModal.create(
  ({frequency, installmentAmountCents, originalDueDate, paymentPlanId, accountId}: ReschedulePlanDialogProps) => {
    const modal = useModal();
    const isMobile = useIsMobile();
    const {pageViewEvent, pageClickEvent} = useAmplitude();
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const updateFutureInstallmentDate = useUpdateFutureInstallmentDate({
      onError: () => {
        toast.error("Failed to reschedule plan");
      },
    });

    useEffect(() => {
      pageViewEvent({page: Pages.RESCHEDULE_PLAN});
    }, []);

    const maxDate = useMemo(() => getMaxDateToReschedule(frequency, originalDueDate), [frequency, originalDueDate]);

    const shouldDisableDate = useMemo(
      () => getShouldDisableDate(frequency, originalDueDate),
      [frequency, originalDueDate]
    );

    const isDateValid = useMemo(
      () => getIsDateValid(selectedDate, dayjs(), maxDate, shouldDisableDate),
      [selectedDate, maxDate, shouldDisableDate]
    );

    const handleConfirm = async () => {
      if (!selectedDate || !isDateValid) return;

      pageClickEvent({page: Pages.RESCHEDULE_PLAN, cta: "update_plan"});
      await updateFutureInstallmentDate.mutateAsync({
        paymentPlanId,
        newDueDate: selectedDate.toDate(),
        accountId,
      });
      toast.success("Plan rescheduled successfully");
      modal.resolve(true);
      await modal.hide();
    };

    return (
      <ReschedulePlanDialogContent
        frequency={frequency}
        installmentAmountCents={installmentAmountCents}
        selectedDate={selectedDate}
        minDate={dayjs()}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        onDateChange={setSelectedDate}
        isMobile={isMobile}
        onConfirm={handleConfirm}
        isLoading={updateFutureInstallmentDate.isLoading}
        isDateValid={isDateValid}
      />
    );
  }
);

export default ReschedulePlanDialog;
